import { router, usePage } from '@inertiajs/react'
import { useEffect } from 'react'
// `toast` comes from the package; @/components/ui/sonner only exports Toaster.
import { toast } from 'sonner'
import type { FlashData } from '@/types'

// A flash can reach us twice for one page: once from the page props on mount,
// and again from the router event Inertia fires after the initial page.set
// settles. Keying each toast by its content makes the second call update the
// first in place instead of stacking a duplicate — and it collapses a repeated
// identical message ("Changes saved." twice in a row) into one refreshed toast.
function showFlash(flash: FlashData) {
  if (flash.alert) toast.error(flash.alert, { id: `alert:${flash.alert}` })
  if (flash.notice) toast.success(flash.notice, { id: `notice:${flash.notice}` })
}

/**
 * Renders Rails flash messages as toasts.
 *
 * Rails has always sent these — `notice` and `alert` are Inertia's default
 * flash keys — but nothing on the client read them, so every "Draft saved."
 * and "Member removed." was invisible.
 *
 * Both sources are kept because neither is reliable alone. On a full page load
 * Inertia fires the flash event itself (`InitialVisit.fireInitialEvents`), but
 * only once `page.set` resolves — which with `encrypt_history` enabled is
 * several macrotasks after this hook has mounted. Drop the effect and a flash
 * arriving before that resolves is missed; drop the listener and client-side
 * flashes never show. Keeping both and de-duplicating by content is correct
 * whichever way the timing falls.
 */
export function useFlash() {
  const { flash } = usePage()

  useEffect(() => {
    showFlash(flash)
  }, [flash])

  useEffect(() => router.on('flash', (event) => showFlash(event.detail.flash)), [])
}
